import { array, bool, object } from 'prop-types';
import React, { Component } from 'react';
import { createRefetchContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import classnames from 'classnames';

import MessageBannerCarousel from './MessageBannerCarousel';

import { isPreview, getPreview, getMessagesToShow } from './messageHelpers';
import { getRegisteredUserType, getUnregisteredUserType } from './userTypeHelpers';

import styles from './MessageBanner-style.scss';

class MessageBanner extends Component {
    constructor(props) {
        super(props);

        this.state = {
            fragmentVariables: {
                fetchMarketingMessagesTransactions: false,
                hasUserType: false,
                setId: '',
                userIds: '',
                userType: '',
            },
            messages: null,
        };
    }

    componentDidUpdate() {
        const { hasRootPendingRefetch, hasUserId, isClient, viewer } = this.props;
        const { messages } = this.state;

        if (!isClient || hasRootPendingRefetch || messages) {
            return;
        }

        if (viewer.messages) {
            this.setState({ messages: getMessagesToShow(viewer.messages) }); // eslint-disable-line react/no-did-update-set-state
            return;
        }

        if (isPreview()) {
            const { messages: previewMessages, setParams } = getPreview();

            if (previewMessages) {
                this.setState({ messages: previewMessages }); // eslint-disable-line react/no-did-update-set-state
                return;
            }

            const { userType, setId = null } = setParams;
            this.fetchMessages({ userType, setId });
        } else {
            const hasAllTransactions = !!viewer.allTransactions;

            if (hasUserId && !hasAllTransactions) {
                this.fetchTransactions();
                return;
            }

            const userType = this.getUserType();

            if (userType) {
                this.getPromoCodeMessages(userType);
                this.fetchMessages({ userType });
            }
        }
    }

    getUserType() {
        const { hasUserId, user, viewer } = this.props;
        let userType;

        if (!!viewer.allTransactions && !!user) {
            userType = getRegisteredUserType(user, viewer);
        } else if (!hasUserId) {
            userType = getUnregisteredUserType();
        }

        return userType;
    }

    getPromoCodeMessages(userType) {
        const { marketingBannerPromoCodes } = window.abExperiments;

        if (marketingBannerPromoCodes) {
            marketingBannerPromoCodes.then((promoCodes = {}) => {
                const messages = promoCodes[userType] || promoCodes.default;

                if (messages) {
                    this.setState({ messages });
                }
            });
        }
    }

    fetchTransactions() {
        if (!this.state.fragmentVariables.fetchMarketingMessagesTransactions) {
            this.setState(
                {
                    fragmentVariables: {
                        ...this.state.fragmentVariables,
                        fetchMarketingMessagesTransactions: true,
                        hasUserId: true,
                        userIds: this.props.userIds,
                    },
                },
                () => {
                    this.props.relay.refetch(this.state.fragmentVariables, null);
                }
            );
        }
    }

    fetchMessages(variables = {}) {
        if (!this.state.fragmentVariables.hasUserType) {
            this.setState(
                {
                    fragmentVariables: {
                        ...this.state.fragmentVariables,
                        ...variables,
                        hasUserType: true,
                    },
                },
                () => {
                    this.props.relay.refetch(this.state.fragmentVariables);
                }
            );
        }
    }

    render() {
        const { isClient, isMobile, isUserTypeTrade } = this.props;
        const { messages } = this.state;

        const classes = classnames({
            [styles.bannerContainer]: true,
            [styles.isTrade]: isUserTypeTrade,
            [styles.isMobile]: isMobile,
        });

        if (!isClient || !messages) {
            return <div className={classes} />;
        }

        return (
            <div className={classes}>
                <MessageBannerCarousel messages={messages} />
            </div>
        );
    }
}

MessageBanner.propTypes = {
    hasRootPendingRefetch: bool,
    hasUserId: bool,
    isMobile: bool,
    isClient: bool,
    isUserTypeTrade: bool,
    relay: object,
    userIds: array,
    user: object,
    viewer: object,
};

const mapStateToProps = ({ header }) => {
    const { isUserTypeTrade } = header;

    return {
        isUserTypeTrade,
    };
};

MessageBanner = createRefetchContainer(
    connect(mapStateToProps)(MessageBanner),
    {
        message: graphql`
            fragment MessageBanner_message on MarketingMessage {
                serviceId
                content {
                    title
                    message
                    cta {
                        text
                        url
                    }
                }
            }
        `,
        user: graphql`
            fragment MessageBanner_user on User {
                isVip
                isVerifiedTrade
                verifiedTradeStatus
                verifiedTradeSubStatus
                tradeFirm {
                    loyalty {
                        status
                    }
                }
            }
        `,
        viewer: graphql`
            fragment MessageBanner_viewer on Viewer
                @argumentDefinitions(
                    hasUserType: { type: "Boolean!", defaultValue: false }
                    setId: { type: "String!", defaultValue: "" }
                    userType: { type: "String!", defaultValue: "" }
                ) {
                messages: messageSet(userType: $userType, setId: $setId)
                    @include(if: $hasUserType) {
                    items {
                        impressionCap
                        note {
                            ...MessageBanner_message @relay(mask: false)
                            anchors {
                                ...MessageBanner_message @relay(mask: false)
                            }
                        }
                    }
                }
                allTransactions: transactions(buyerId: $userIds, first: 0)
                    @include(if: $fetchMarketingMessagesTransactions) {
                    totalResults
                }
                initiatedTransactions: transactions(
                    buyerId: $userIds
                    viewName: "buyerTracking.initiated"
                    first: 0
                ) @include(if: $fetchMarketingMessagesTransactions) {
                    totalResults
                }
                submittedTransactions: transactions(
                    buyerId: $userIds
                    viewName: "buyerTracking.submitted"
                    first: 0
                ) @include(if: $fetchMarketingMessagesTransactions) {
                    totalResults
                }
                confirmedTransactions: transactions(
                    buyerId: $userIds
                    sort: "createdDate asc"
                    viewName: "buyerTracking.confirmed"
                    first: 0
                ) @include(if: $fetchMarketingMessagesTransactions) {
                    totalResults
                }
            }
        `,
    },
    graphql`
        query MessageBannerRefetchQuery(
            $fetchMarketingMessagesTransactions: Boolean!
            $hasUserType: Boolean!
            $setId: String!
            $userIds: [String!]
            $userType: String!
        ) {
            viewer {
                ...MessageBanner_viewer
                    @arguments(hasUserType: $hasUserType, setId: $setId, userType: $userType)
            }
        }
    `
);

export default MessageBanner;
